import { action, computed, observable } from 'mobx';
import { setter, toggle } from 'mobx-decorators';
import { concatDeserializeMappers, deserializeWithMappers, setFieldValues } from 'stores/Utils';
import ColorStore from 'stores/Model/ColorStore';
import BackgroundStore from 'stores/Model/BackgroundStore';
import BorderStore from 'stores/Model/BorderStore';
import FontStore from 'stores/Model/FontStore';
import BaseBlockStore from '../BaseBlockStore';
import pageStore from 'stores/PageStore';
import constants from 'Constants';

class ButtonBlockStore extends BaseBlockStore {

  @setter @observable text;
  @setter @toggle @observable isBold;
  @setter @toggle @observable isItalic;
  @setter @toggle @observable isUnderline;

  @setter @observable textSize;
  @setter @observable textColor;
  @setter @observable font;
  @setter @observable background;
  @setter @observable border;
  @setter @observable align;
  @setter @observable widthType;
  @setter @observable horizontalPadding;
  @setter @observable verticalPadding;
  @setter @observable maxWidth;
  @setter @observable hoverEffect;

  @setter @observable action;
  // Decline path ("No thanks") — modifies action, doesn't replace it, because a
  // decline still needs somewhere to go (usually straight to the reward page).
  @setter @toggle @observable skipForm;
  @setter @observable actionPageId;
  @setter @observable actionUrl;
  @setter @toggle @observable actionUrlNewWindow;
  @setter @toggle @observable recordActionAsEngagement;

  static create(data={}) {
    const instance = new ButtonBlockStore();
    setFieldValues(instance, data);
    return instance;
  }

  static getDefaults() {
    return Object.assign({}, BaseBlockStore.getDefaults(), {
      "text": "",
      "isBold": false,
      "isItalic": false,
      "isUnderline": false,

      "textSize": "",
      "textColor": new ColorStore(),
      "font": new FontStore(),
      "background": new BackgroundStore(),
      "border": new BorderStore(),
      "align": "",
      "widthType": "",
      "horizontalPadding": "",
      "verticalPadding": "",
      // BLANK MEANS BLANK - an unset cap applies no constraint. Never default this to a
      // number; the console's ButtonBlockStore.max_width carries the same contract.
      "maxWidth": "",
      // Mirrors the console's default so a button saved before this field existed
      // renders the same effect live as it does in the editor preview.
      "hoverEffect": constants.PROMPT_HOVER_EFFECT_SHINE,

      "action": "",
      "skipForm": false,
      "actionPageId": "",
      "actionUrl": "",
      "actionUrlNewWindow": false,
      "recordActionAsEngagement": false,
    });
  }

  constructor(defaults=ButtonBlockStore.getDefaults()) {
    super(defaults);
  }

  reset(defaults=ButtonBlockStore.getDefaults()) {
    super.reset(defaults);
  }

  getDeserializeMappers() {
    let mappers = [
      {"field": "text"},
      {"field": "isBold", "accessor": "is_bold"},
      {"field": "isItalic", "accessor": "is_italic"},
      {"field": "isUnderline", "accessor": "is_underline"},

      {"field": "textSize", "accessor": "text_size"},
      {"field": "textColor", "accessor": "text_color", "type": constants.DESERIALIZE_TYPE_OBJECT, "cls": ColorStore},
      {"field": "font", "accessor": "font", "type": constants.DESERIALIZE_TYPE_OBJECT, "cls": FontStore},
      {"field": "background", "accessor": "background", "type": constants.DESERIALIZE_TYPE_OBJECT, "cls": BackgroundStore},
      {"field": "border", "type": constants.DESERIALIZE_TYPE_OBJECT, "cls": BorderStore},
      {"field": "align"},
      {"field": "widthType", "accessor": "width_type"},
      {"field": "horizontalPadding", "accessor": "horizontal_padding"},
      {"field": "verticalPadding", "accessor": "vertical_padding"},
      {"field": "maxWidth", "accessor": "max_width"},
      {"field": "hoverEffect", "accessor": "hover_effect"},

      {"field": "action"},
      {"field": "skipForm", "accessor": "skip_form"},
      {"field": "actionPageId", "accessor": "action_page_id"},
      {"field": "actionUrl", "accessor": "action_url"},
      {"field": "actionUrlNewWindow", "accessor": "action_url_new_window"},
      {"field": "recordActionAsEngagement", "accessor": "record_action_as_engagement"},
    ];

    return concatDeserializeMappers(super.getDeserializeMappers(), mappers);
  }

  @action async deserialize(data) {
    await deserializeWithMappers(this, this.getDeserializeMappers(), data);
  }

  @computed get alignLeft() {
    return this.align === constants.CSS_ALIGN_LEFT;
  }

  @computed get alignCenter() {
    return this.align === constants.CSS_ALIGN_CENTER;
  }

  @computed get alignRight() {
    return this.align === constants.CSS_ALIGN_RIGHT;
  }

  @computed get alignCss() {
    if (this.alignLeft) return "left";
    if (this.alignCenter) return "center";
    if (this.alignRight) return "right";
    return "";
  }

  @computed get hoverEffectNone() {
    return this.hoverEffect === constants.PROMPT_HOVER_EFFECT_NONE;
  }

  @computed get hoverEffectPress() {
    return this.hoverEffect === constants.PROMPT_HOVER_EFFECT_PRESS;
  }

  @computed get hoverEffectShine() {
    return this.hoverEffect === constants.PROMPT_HOVER_EFFECT_SHINE;
  }

  // Shine sits on top of press, so both selections get the press affordance.
  @computed get hoverEffectClassName() {
    if (this.hoverEffectPress) return "ov-button-effect-press";
    if (this.hoverEffectShine) return "ov-button-effect-press ov-button-effect-shine";
    return "";
  }

  @computed get widthTypeHorizontalPadding() {
    return this.widthType === constants.PROMPT_BUTTON_WIDTH_TYPE_HORIZONTAL_PADDING;
  }

  @computed get widthTypeFull() {
    return this.widthType === constants.PROMPT_BUTTON_WIDTH_TYPE_FULL;
  }

  @computed get href() {
    if (this.actionOpenUrl) return this.actionUrl;
    return '';
  }

  @computed get actionNextPage() {
    return this.action === constants.PROMPT_BUTTON_ACTION_NEXT_PAGE;
  }

  @computed get actionGoToPage() {
    return this.action === constants.PROMPT_BUTTON_ACTION_GO_TO_PAGE;
  }

  @computed get actionGoToCart() {
    return this.action === constants.PROMPT_BUTTON_ACTION_GO_TO_CART;
  }

  @computed get actionGoToCheckout() {
    return this.action === constants.PROMPT_BUTTON_ACTION_GO_TO_CHECKOUT;
  }

  @computed get actionOpenUrl() {
    return this.action === constants.PROMPT_BUTTON_ACTION_OPEN_URL;
  }

  @computed get actionClose() {
    return this.action === constants.PROMPT_BUTTON_ACTION_CLOSE;
  }

  @computed get preventDefault() {
    if (this.actionNextPage) return true;
    if (this.actionGoToPage) return true;
    if (this.actionClose) return true;
    return false
  }

  @computed get fontOptions() {
    if (this.font.option) return [this.font.option];
    return [];
  }

  @computed get elementAttributes() {
    let attrs = {};

    if (this.actionGoToCart) {
      attrs['href'] = pageStore.cartUrl;
    }

    if (this.actionGoToCheckout) {
      attrs['href'] = pageStore.checkoutUrl;
    }

    if (this.actionOpenUrl) {
      attrs['href'] = this.href;
      if (this.actionUrlNewWindow) attrs['target'] = "_blank";
    }

    return attrs;
  }

  @computed get buttonBlockCssAttributes() {
    let attrs = {};

    if (this.widthTypeHorizontalPadding) {
      if (this.alignCenter) attrs['justify-content'] = "center";
      if (this.alignLeft) attrs['justify-content'] = "flex-start";
      if (this.alignRight) attrs['justify-content'] = "flex-end";
    }
    return attrs;
  }

  @computed get cssAttributes() {
    let attrs = {
      "display": `inline-block`,
      "color": this.textColor.css,
      "font-size": `${this.textSize}px`,
      "font-weight": this.isBold ? 'bold' : 'normal',
      "font-style": this.isItalic ? 'italic' : 'normal',
      "font-family": `${this.font.familyCss}`,
      "text-decoration": this.isUnderline ? 'underline' : 'none',
      "padding": `${this.verticalPadding}px ${this.horizontalPadding}px ${this.verticalPadding}px ${this.horizontalPadding}px`,
    };

    if (this.widthTypeFull) {
      // ⛔ NOT `0` HORIZONTALLY. A full-width button fills its COLUMN, and a column can be
      // narrow - measured on Home Decor Star Rating's Thanks page, a 278px button in a bar
      // layout's right column whose label "BACK TO SHOPPING" spanned x720-x998 exactly: the B
      // and the final G touched the edges. `box-sizing: border-box` means this inset changes
      // the button's outer size by nothing at all; it only stops the label reaching the edge.
      attrs['padding'] = `${this.verticalPadding}px 12px`;
      attrs['width'] = "100%";
    }

    // Independent of widthType - caps the rendered width whether the button is FULL (fills
    // a column that can itself be the viewport on a full-screen archetype) or
    // HORIZONTAL_PADDING. Blank means blank: unset applies nothing.
    if (this.maxWidth) {
      attrs['max-width'] = `${this.maxWidth}px`;
    }

    attrs = {...attrs, ...this.background.cssAttributes};
    attrs = {...attrs, ...this.border.cssAttributes};
    return attrs;
  }
}


export default ButtonBlockStore;
